import { StaticPageLayout } from '@/components/StaticPageLayout';
import { Testimonials } from '@/components/Testimonials';
import { ArrowRight, Video, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@inertiajs/react';

export default function HomePage() {
  const stats = [
    { value: '3,000+', label: 'mentees guided' },
    { value: '1,500+', label: 'volunteer mentors' },
    { value: 'Hundreds', label: 'admitted to programs' },
  ];

  const services = [
    {
      number: '01',
      title: 'Personal Statement Review',
      description:
        'Receive at least two rounds of detailed feedback on personal statements and other written application materials.',
    },
    {
      number: '02',
      title: 'Practice Interviews',
      description:
        'Prepare for interviews with your mentor and with others. We regularly host individual and group practice interviews.',
    },
    {
      number: '03',
      title: 'Professional Networking',
      description:
        'Join an international group of students and professionals committed to increasing diversity in STEM.',
    },
  ];

  return (
    <StaticPageLayout>
      {/* Hero Section - Editorial Style */}
      <section className="bg-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="py-24 md:py-32">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="mb-8">
                  <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">
                    501(c)(3) Non-Profit Organization
                  </p>
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-light leading-tight mb-6">
                    <span className="font-normal">Democratizing</span>
                    <br />
                    <span className="italic">access to</span>
                    <br />
                    <span className="font-semibold text-primary">
                      higher education
                    </span>
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-700 leading-relaxed max-w-2xl">
                    The first student-led organization committed to shrinking
                    the socioeconomic gap in medical and graduate school — one
                    application at a time.
                  </p>
                </div>
                {/* <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-base font-normal"
                    asChild
                  >
                    <Link href="/registration/new">
                      Start Your Application
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-primary text-primary hover:bg-primary/5 px-8 py-6 text-base font-normal"
                    asChild
                  >
                    <Link href="/how-it-works">Learn More</Link>
                  </Button>
                </div> */}
              </div>
              <div className="max-w-[480px] lg:col-span-5 relative">
                {/* Creative visualization: Journey through application maze */}
                <div className="relative h-[500px] flex items-center justify-center">
                  {/* Starting point - abstract student representation */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2">
                    <div className="relative">
                      {/* Overlapping circles representing diversity */}
                      <div className="absolute w-20 h-20 bg-blue-200/30 rounded-full"></div>
                      <div className="absolute w-20 h-20 bg-purple-200/30 rounded-full left-3 top-3"></div>
                      <div className="absolute w-20 h-20 bg-pink-200/30 rounded-full left-6"></div>
                      <div className="w-24 h-24 flex items-center justify-center relative z-10">
                        <div className="text-xs text-gray-600 font-medium bg-white/80 px-3 py-1 rounded-full shadow-sm">
                          You
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Destination - future possibilities */}
                  <div className="absolute right-0 top-1/2 -translate-y-1/2">
                    <div className="relative">
                      {/* Radiating lines suggesting growth */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-32 h-32">
                          <svg className="w-full h-full" viewBox="0 0 100 100">
                            <g className="opacity-20">
                              <line
                                x1="50"
                                y1="50"
                                x2="50"
                                y2="10"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="80"
                                y2="20"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="90"
                                y2="50"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="80"
                                y2="80"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="50"
                                y2="90"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="20"
                                y2="80"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="10"
                                y2="50"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                              <line
                                x1="50"
                                y1="50"
                                x2="20"
                                y2="20"
                                stroke="rgb(147 51 234)"
                                strokeWidth="1"
                              />
                            </g>
                          </svg>
                        </div>
                      </div>
                      {/* Central core */}
                      <div className="relative w-32 h-32 flex items-center justify-center">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 rounded-full animate-pulse"></div>
                        <div className="relative bg-white/90 rounded-full p-6 shadow-sm">
                          <GraduationCap className="w-8 h-8 text-primary/60" />
                        </div>
                      </div>
                      {/* Orbiting possibilities */}
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                        <div className="w-2 h-2 bg-primary/30 rounded-full"></div>
                      </div>
                      <div className="absolute top-1/2 -right-2 -translate-y-1/2">
                        <div className="w-2 h-2 bg-primary/30 rounded-full"></div>
                      </div>
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                        <div className="w-2 h-2 bg-primary/30 rounded-full"></div>
                      </div>
                    </div>
                  </div>

                  {/* Bridge made of floating documents */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {/* Personal Statement */}
                    <div className="absolute left-[20%] top-[45%] transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                      <div className="w-24 h-32 bg-white border border-gray-200 shadow-lg p-2 cursor-pointer">
                        <div className="text-[7px] text-gray-400 mb-1">
                          Personal Statement
                        </div>
                        <div className="space-y-1">
                          <div className="h-0.5 bg-gray-300 w-full"></div>
                          <div className="h-0.5 bg-gray-300 w-4/5"></div>
                          <div className="h-0.5 bg-gray-300 w-full"></div>
                          <div className="h-0.5 bg-primary/40 w-3/4"></div>
                          <div className="h-0.5 bg-primary/40 w-5/6"></div>
                        </div>
                      </div>
                    </div>

                    {/* Research/Experience */}
                    <div className="absolute left-[30%] top-[58%] transform -rotate-3 hover:-rotate-20 transition-transform duration-300">
                      <div className="w-20 h-24 bg-white border border-gray-200 shadow-lg p-2 cursor-pointer">
                        <div className="text-[7px] text-gray-400 font-medium mb-1 text-center">
                          Research
                        </div>
                        <div className="space-y-1 mt-2">
                          <div className="h-0.5 bg-gray-300 w-full"></div>
                          <div className="h-0.5 bg-primary/30 w-4/5"></div>
                          <div className="h-0.5 bg-gray-300 w-3/5"></div>
                        </div>
                      </div>
                    </div>

                    {/* Letters of Rec */}
                    <div className="absolute left-[50%] top-[48%] transform -translate-x-1/2 rotate-3 hover:rotate-0 transition-transform duration-300">
                      <div className="w-24 h-28 bg-white border border-gray-200 shadow-lg p-2 cursor-pointer">
                        <div className="text-[7px] text-gray-400 mb-1">
                          Letter of Rec
                        </div>
                        <div className="space-y-1">
                          <div className="h-0.5 bg-gray-300 w-full"></div>
                          <div className="h-0.5 bg-gray-300 w-4/5"></div>
                          <div className="h-0.5 bg-primary/40 w-full"></div>
                        </div>
                      </div>
                    </div>

                    {/* CV/Resume */}
                    <div className="absolute right-[35%] top-[52%] transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                      <div className="w-20 h-24 bg-white border border-gray-200 shadow-lg p-2 cursor-pointer">
                        <div className="text-[7px] text-gray-400 mb-1">
                          CV/Resume
                        </div>
                        <div className="mt-1 space-y-1">
                          <div className="h-0.5 bg-gray-300 w-full"></div>
                          <div className="h-0.5 bg-gray-300 w-4/5"></div>
                          <div className="h-0.5 bg-primary/30 w-full"></div>
                          <div className="h-0.5 bg-gray-300 w-3/5"></div>
                        </div>
                      </div>
                    </div>

                    {/* Interview Prep */}
                    <div className="absolute right-[20%] top-[46%] transform rotate-12 hover:rotate-0 transition-transform duration-300">
                      <div className="w-24 h-20 bg-white border border-gray-200 shadow-lg p-2 cursor-pointer">
                        <div className="text-[7px] text-gray-400 mb-1">
                          Interview Prep
                        </div>
                        <div className="flex justify-center items-center h-8">
                          <Video className="w-6 h-6 text-primary/40" />
                        </div>
                      </div>
                    </div>

                    {/* Mentor connection line */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      <path
                        d="M 100 250 Q 250 200 400 250"
                        stroke="url(#purpleGradient)"
                        strokeWidth="2"
                        fill="none"
                        strokeDasharray="5,5"
                        className="animate-pulse"
                      />
                      <defs>
                        <linearGradient
                          id="purpleGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop offset="0%" stopColor="rgb(147 51 234 / 0.3)" />
                          <stop
                            offset="50%"
                            stopColor="rgb(147 51 234 / 0.6)"
                          />
                          <stop
                            offset="100%"
                            stopColor="rgb(147 51 234 / 0.3)"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Mentor presence as glowing orbs guiding the path */}
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Glowing mentor guides */}
                    <div className="absolute top-[15%] left-[20%]">
                      <div className="w-3 h-3 bg-primary/40 rounded-full blur-sm animate-pulse"></div>
                    </div>
                    <div className="absolute top-[30%] left-[35%]">
                      <div
                        className="w-4 h-4 bg-primary/50 rounded-full blur-sm animate-pulse"
                        style={{ animationDelay: '0.5s' }}
                      ></div>
                    </div>
                    <div className="absolute top-[20%] right-[40%]">
                      <div
                        className="w-3 h-3 bg-primary/40 rounded-full blur-sm animate-pulse"
                        style={{ animationDelay: '1s' }}
                      ></div>
                    </div>
                    <div className="absolute bottom-[30%] right-[25%]">
                      <div
                        className="w-4 h-4 bg-primary/50 rounded-full blur-sm animate-pulse"
                        style={{ animationDelay: '1.5s' }}
                      ></div>
                    </div>
                    <div className="absolute bottom-[35%] left-[45%]">
                      <div
                        className="w-3 h-3 bg-primary/40 rounded-full blur-sm animate-pulse"
                        style={{ animationDelay: '2s' }}
                      ></div>
                    </div>
                  </div>

                  {/* Tagline */}
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center">
                    <p className="text-sm text-gray-400">
                      Every journey is unique — we're here to guide yours
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Minimal */}
      <section className="border-t border-gray-200">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            {stats.map((stat, index) => (
              <div key={index} className="py-12 md:px-8 text-center">
                <div className="text-4xl md:text-5xl font-light text-gray-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm uppercase tracking-wider text-gray-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="max-w-4xl">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-6">
              Our Mission
            </h2>
            <p className="text-3xl md:text-4xl font-light leading-relaxed text-gray-800">
              The cost of professional admissions advising is an
              <span className="italic"> enormous barrier</span> for students
              pursuing careers in STEM. Project SHORT strives to{' '}
              <span className="font-semibold text-primary">
                demystify this process
              </span>{' '}
              by matching students one-on-one with graduate student mentors in
              their field —<span className="text-primary"> all for free</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section - Editorial Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="mb-16">
            <h2 className="text-sm uppercase tracking-widest text-primary mb-4">
              What We Offer
            </h2>
            <p className="text-4xl md:text-5xl font-light text-gray-900">
              Comprehensive support,
              <br />
              <span className="italic text-primary">zero cost</span>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-x-12 gap-y-16">
            {services.map((service, index) => (
              <div key={index} className="group">
                <div className="mb-6">
                  <span className="text-6xl font-light text-primary/20 group-hover:text-primary/30 transition-colors">
                    {service.number}
                  </span>
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* <div className="mt-16 p-12 bg-primary/5 border border-primary/10"> */}
          {/*   <div className="text-center"> */}
          {/*     <div className="text-5xl md:text-6xl font-light text-primary mb-4"> */}
          {/*       100% Free */}
          {/*     </div> */}
          {/*     <p className="text-lg text-gray-600"> */}
          {/*       100% of the time. No hidden fees, no premium tiers. */}
          {/*     </p> */}
          {/*   </div> */}
          {/* </div> */}
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section - Split Design */}
      <section className="bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid md:grid-cols-2 border-t border-gray-200">
            <div className="py-20 pr-8 border-b md:border-b-0 md:border-r border-gray-200">
              <h3 className="text-sm uppercase tracking-widest text-primary mb-4">
                For Applicants
              </h3>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Apply to be a mentee
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Application cycles are open based on whether you are applying to
                a graduate or medical school.
              </p>
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-base font-normal"
                asChild
              >
                <Link href="/registration/new">
                  Apply as a Mentee
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="py-20 pl-0 md:pl-8">
              <h3 className="text-sm uppercase tracking-widest text-primary mb-4">
                For Professionals
              </h3>
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Apply to be a mentor
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Share your knowledge about applications to help the next
                generation of scientists and physicians.
              </p>
              <Button
                size="lg"
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5 px-8 py-6 text-base font-normal"
                asChild
              >
                <Link href="/registration/new">
                  Apply as a Mentor
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}
